/**
 * Agreement → Portfolios (Banks arc step 4, migration 0115; backend
 * `routers/agreement_portfolios.py`). A client portfolio (bank + number) is linked to at most
 * one agreement at a time (Pedro P2). Writes go through maker-checker like agreement parties:
 * an RM's link / unlink is filed for Compliance (`pending`); Admin and Compliance apply at once.
 */
import { api } from './client';
import type { ConsolidationRef, ModelRef } from './portfolioConsolidations';
import type { PendingChangeRequestRef, UUID, WriteResult } from './types';

export interface AgreementPortfolioLink {
  id: UUID;
  agreement_id: UUID; agreement_label: string;
  bank_id: UUID; bank_mnemonic: string | null;
  portfolio_number: string; portfolio_key: string;
  /** "<bank id>:<number>" — the Portfolios API's name for it */
  ref: string;
  /** "BNP · 000123456" */
  label: string;
  latest_date: string | null;
  pending_change_requests: PendingChangeRequestRef[];
  /** TRADE-CONS 1a: the consolidation it is in, if any */
  consolidation: ConsolidationRef | null;
  /** 1b: its own model (only outside a consolidation) */
  model: ModelRef | null;
}
export interface LinkRef { id: UUID | null; agreement_id: UUID | null; agreement_label: string; cr_id: UUID | null }
export interface PortfolioLinkRow {
  ref: string; label: string; bank_id: UUID | null; bank_mnemonic: string | null;
  portfolio_number: string; portfolio_key: string; latest_date: string;
  link: LinkRef | null;
  pending_links: LinkRef[];
  pending_unlink: LinkRef | null;
  /** TRADE-CONS 1a: the consolidation it is in, if any */
  consolidation: ConsolidationRef | null;
  /** 1b: its own model (only outside a consolidation) */
  model: ModelRef | null;
}
export interface PortfolioLinks { items: PortfolioLinkRow[]; total: number; linked: number; unlinked: number }
export interface LinkInput { agreement_id: UUID; bank_id: UUID; portfolio_number: string }

async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const agreementPortfoliosApi = {
  list: (agreementId: UUID) =>
    api.get<AgreementPortfolioLink[]>(`/agreement-portfolios?agreement_id=${encodeURIComponent(agreementId)}`),
  overview: (opts: { q?: string; unlinked?: boolean } = {}) => {
    const u = new URLSearchParams();
    if (opts.q) u.set('q', opts.q);
    if (opts.unlinked) u.set('unlinked', 'true');
    const s = u.toString();
    return api.get<PortfolioLinks>(`/agreement-portfolios/overview${s ? `?${s}` : ''}`);
  },
  link: (p: LinkInput) => writeWithMaker(() => api.postMeta<AgreementPortfolioLink>('/agreement-portfolios', p)),
  unlink: (id: UUID) => writeWithMaker(() => api.deleteMeta<AgreementPortfolioLink>(`/agreement-portfolios/${id}`)),
};
